import { Loader } from '@123-front/ui-kit';
import PropTypes from 'prop-types';
import React, { Suspense, lazy, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { useLocation } from 'react-router-dom';
import i123n from '../../i18n';
import { getStore } from '../../store';
import { domainResolver } from '../../utils/domain-resolver';
import { defaultFlux, detectActiveFlux, getFluxIndex } from '../../utils/fluxes';
import { getKeyByValue } from '../../utils/object';
import { useBaseUrl } from '../../utils/useBaseUrl';
import { useFluxObj } from '../../utils/useFluxObj';
import { analyticsPushEvent, analyticsPushPage } from '../analytics/store/analytics.actions';
import { LoadingPage } from '../main/components/LoadingPage';
import history from '../main/store/history';
import Service from '../service';
import {
  changeFlux,
  getProductStepsConfig,
  moveToNextStep,
  moveToNextStepV2,
  resetProgress,
  completeChangeFlux,
} from './store/wizard.actions';
import {
  getModuleName,
  getModuleNameV2,
  getStepsList,
  useActiveFluxSteps,
} from './store/wizard.store.utils';
import { wizardReducerPropTypes } from './wizard.proptypes';

const Wizard = ({
  product,
  getProductStepsConfig,
  moveToNextStep,
  moveToNextStepV2,
  resetProgress,
  changeFlux,
  completeChangeFlux,
  wizardState,
  analyticsPushEvent,
  analyticsPushPage,
  partnerConfig,
  currentStepV2,
  stepsV2,
}) => {
  const store = getStore();
  useTranslation();
  const pathName = useLocation().pathname;
  const currentUrlTokens = pathName.split('/');
  const currentPage = currentUrlTokens[currentUrlTokens.length - 1];
  const activeFluxName = detectActiveFlux();
  const flux = useFluxObj(activeFluxName);
  const { routeNames, routeNamesV2 } = i123n.getDataByLanguage(domainResolver()).translation[
    product
  ];

  const isCurrentFluxV2 = Boolean(currentStepV2) || !Array.isArray(wizardState?.steps);
  const getV2StepData = (stepName) => stepsV2?.[stepName] || {};
  const currentStepDataV2 = getV2StepData(currentStepV2);

  // NOTE: `resolvedComponentNameFromUri` is obtained by finding which key translates to `currentPage`
  // and v1 and v2 are configured separately: See routeNames and routeNamesV2 in translations/ar.json

  const componentNameToSlugMapping = isCurrentFluxV2 ? routeNamesV2[flux?.index] : routeNames.pages;
  const resolvedComponentNameFromUri =
    componentNameToSlugMapping && getKeyByValue(componentNameToSlugMapping, currentPage);

  const baseUrl = useBaseUrl();
  const activeFluxSteps = useActiveFluxSteps();

  useEffect(() => {
    if (partnerConfig) {
      const stepsToSkip = new Set(partnerConfig?.skipSteps);

      if (stepsToSkip.size === 0) {
        return;
      }

      activeFluxSteps.map(
        (module) =>
          (module.moduleSteps = module.moduleSteps.filter((step) => !stepsToSkip.has(step))),
      );
    }
  }, [partnerConfig, activeFluxSteps]);

  const [injectedProduct, setInjectedProduct] = useState(false);
  const setProductStore = useCallback(async () => {
    const productStore = await import(`../${product}/${product}.reducer`).then(
      (moduleStore) => moduleStore.default,
    );
    store.injectReducer(product, productStore);
    setInjectedProduct(true);
  }, [product, store]);

  useEffect(() => {
    setProductStore();
  }, [setProductStore]);

  const handleNextStepV2 = (selectedBranch) => {
    // NOTE: typeof string is checked because v1 components do the following:
    // `<button onClick={handleNextStep}/>`
    // resulting in the argument to be the JS.click event

    const wasAlternativeBranchSelected = selectedBranch && typeof selectedBranch === 'string';
    const nextStep = wasAlternativeBranchSelected
      ? currentStepDataV2.branches?.[selectedBranch]
      : currentStepDataV2.next;

    const nextComponentName = getV2StepData(nextStep).component;
    const nextRoute = `./${componentNameToSlugMapping[nextComponentName]}${window.location.search}`;
    moveToNextStepV2(nextRoute, nextStep);
  };

  const handleNextStep = () => {
    const nextComponentName = getStepsList(activeFluxSteps)[wizardState.completedStep + 1];
    const nextRoute = `./${componentNameToSlugMapping[nextComponentName]}${window.location.search}`;
    moveToNextStep(nextRoute);
  };

  const handleChangeFlux = (toFlux, toStep = 0) => {
    changeFlux(baseUrl, toFlux, toStep);
  };

  const handleGetFluxIndex = (fluxName) => {
    return getFluxIndex(wizardState.fluxes, fluxName);
  };

  const loadComponent = (getModuleName) => {
    const Component = lazy(async () => {
      const moduleName = getModuleName(
        activeFluxSteps,
        resolvedComponentNameFromUri,
        stepsV2,
        currentStepV2,
      );
      return import(`../${moduleName}/${resolvedComponentNameFromUri}`)
        .then(async (module) => {
          const moduleLoaded = moduleName in store.getState();

          if (!moduleLoaded) {
            const moduleStore = await import(`../${moduleName}/${moduleName}.reducer`).then(
              (moduleStore) => moduleStore.default,
            );
            store.injectReducer(moduleName, moduleStore);
          }

          return module;
        })
        .catch((e) => console.error('Error loading component', e));
    });
    return (
      <Service
        handleNextStep={isCurrentFluxV2 ? handleNextStepV2 : handleNextStep}
        handleChangeFlux={handleChangeFlux}
        handleGetFluxIndex={handleGetFluxIndex}
        ComponentToRender={Component}
        componentName={resolvedComponentNameFromUri}
      />
    );
  };

  const v2StepResolver = () => {
    const currentComponent = currentStepDataV2.component;
    const expectedPage = componentNameToSlugMapping[currentComponent];
    const searchParams = window.location.search;

    // This means that no step was provided so the root is loaded
    if (!currentPage || currentPage === flux.index) {
      const addFlux = !pathName.endsWith('/');
      const fluxRoute = addFlux ? `${flux.index}/` : '';
      const nextRoute = `./${fluxRoute}${expectedPage}${searchParams}`;
      moveToNextStepV2(nextRoute, 'root');
      return;
    }

    const isKnownRoute = Object.values(componentNameToSlugMapping).includes(currentPage);
    if (!isKnownRoute) {
      history.push(`${baseUrl}/not-found${searchParams}`);
      return;
    }

    if (!wizardState.historyV2) {
      const nextRoute = `./${expectedPage}${searchParams}`;
      moveToNextStepV2(nextRoute, 'root');
      return;
    }

    // This is responsible for browser arrows' behavior.
    const componentNameFromState = currentComponent;
    if (componentNameFromState === resolvedComponentNameFromUri) {
      return;
    }

    const nameOfPreviousStepWithMatchingComponent = [...(wizardState.historyV2 || [])]
      .reverse()
      .find((stepName) => getV2StepData(stepName).component === resolvedComponentNameFromUri);

    if (nameOfPreviousStepWithMatchingComponent) {
      // If the component name resolved from the Uri matches any of the past steps
      // Then update state to match it and don't change the Uri.
      moveToNextStepV2(undefined, nameOfPreviousStepWithMatchingComponent);
      return;
    }

    return;
  };

  const v1StepResolver = () => {
    // If there are fluxes in the state
    const fluxesKeys = wizardState.fluxes.map((flux) => flux.index);
    let activeFlux = detectActiveFlux();
    const existsFlux = fluxesKeys.includes(activeFlux);

    if (!existsFlux) {
      // Reset flux number to default if it doesn't exist in fluxes
      analyticsPushEvent(
        'trackError',
        'wizard not found',
        'error-campo',
        { url: pathName },
        'cotizador',
      );

      activeFlux = defaultFlux;
    }

    const stepsList = getStepsList(activeFluxSteps);
    const transStepsList = stepsList.map((step) => componentNameToSlugMapping[step]);
    const stepIdx = stepsList.indexOf(resolvedComponentNameFromUri);

    if (wizardState.hasChangeFlux && stepIdx !== wizardState.completedStep) {
      return;
    }
    if (stepIdx === -1) {
      // step not found or empty
      if (
        existsFlux &&
        ![`${baseUrl} /${activeFlux}`, `${baseUrl}/${activeFlux} /`].includes(pathName)
      ) {
        // url doesn't specify component/step name. e.g. /cotizar/auto/0
        analyticsPushEvent(
          'trackError',
          'wizard not found',
          'error-campo',
          { url: pathName },
          'cotizador',
        );
      }
      resetProgress();
      if (!window.location.pathname.endsWith('/not-found')) {
        history.push(
          `${baseUrl}/${activeFlux}/${transStepsList[0] || ''}${window.location.search}`,
        );
      }
    } else if (stepIdx > wizardState.completedStep && !wizardState.hasChangeFlux) {
      // If url matches future step but hasn't completed the previous ones
      // or has changed flux.
      // Then: Reset the flux
      if (flux.isPartnerFlux) {
        history.push(`${baseUrl}/${activeFlux}/${window.location.search}`);
      } else {
        history.push(
          `${baseUrl}/${activeFlux}/${transStepsList[wizardState.currentStep - 1]}${
            window.location.search
          }`,
        );
      }
    } else if (stepIdx < wizardState.completedStep) {
      // step behind
      analyticsPushEvent('trackEvent', `back-page-${currentPage}`, 'cotizacion', {
        url: pathName,
      });
      analyticsPushPage(product, resolvedComponentNameFromUri);
      resetProgress(stepIdx);
    } else if (stepIdx === wizardState.completedStep) {
      // this is the right step
      if (
        (wizardState.hasChangeFlux && wizardState.activeFlux === flux.index) ||
        !wizardState.hasChangeFlux
      ) {
        analyticsPushPage(product, resolvedComponentNameFromUri);
      }
      if (wizardState.hasChangeFlux) {
        completeChangeFlux();
      }
    } else if (wizardState.hasChangeFlux) {
      resetProgress(stepIdx);
    }
  };

  useEffect(() => {
    if (isCurrentFluxV2) {
      return v2StepResolver();
    }

    if (wizardState.product !== product) {
      getProductStepsConfig(product);
    }

    if (wizardState.fluxes.length) {
      return v1StepResolver();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    product,
    getProductStepsConfig,
    resetProgress,
    changeFlux,
    analyticsPushEvent,
    analyticsPushPage,
    pathName,
    resolvedComponentNameFromUri,
    routeNames,
    wizardState.steps,
    flux,
  ]);

  return (
    <section>
      {injectedProduct && (wizardState.steps?.length > 0 || wizardState.stepsV2) ? (
        <Suspense fallback={<LoadingPage />}>
          {loadComponent(isCurrentFluxV2 ? getModuleNameV2 : getModuleName)}
        </Suspense>
      ) : (
        <Loader className={'loading'} />
      )}
    </section>
  );
};

Wizard.propTypes = {
  product: PropTypes.string,
  getProductStepsConfig: PropTypes.func,
  moveToNextStep: PropTypes.func,
  moveToNextStepV2: PropTypes.func,
  resetProgress: PropTypes.func,
  changeFlux: PropTypes.func,
  completeChangeFlux: PropTypes.func,
  wizardState: wizardReducerPropTypes,
  analyticsPushEvent: PropTypes.func,
  analyticsPushPage: PropTypes.func,
  partnerConfig: PropTypes.object,
  currentStepV2: PropTypes.string,
  stepsV2: PropTypes.object,
};

const mapStateToProps = (state) => ({
  wizardState: state.wizard,
  partnerConfig: state.service.partnerConfig.data,
  currentStepV2: state.wizard.currentStepV2,
  stepsV2: state.wizard.stepsV2,
});

export default connect(mapStateToProps, {
  getProductStepsConfig,
  moveToNextStep,
  moveToNextStepV2,
  resetProgress,
  changeFlux,
  completeChangeFlux,
  analyticsPushEvent,
  analyticsPushPage,
})(Wizard);
